import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

/**
 * Custom rendering options for Contentful rich text
 * Handles embedded assets (images), entries, and custom styling
 */
const richTextRenderOptions: Options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const targetFields = node.data?.target?.fields;
      if (!targetFields) return null;

      // Handle both localized and non-localized field structures
      const fileField = targetFields.file;
      const titleField = targetFields.title;
      const descriptionField = targetFields.description;

      // Extract URL from localized or direct field
      const imageUrl =
        fileField?.url ||
        (fileField as any)?.[0]?.url ||
        (fileField as any)?.['en-US']?.url;

      // Extract title from localized or direct field
      const title =
        (typeof titleField === 'string'
          ? titleField
          : (titleField as any)?.['en-US']) || '';

      // Extract description from localized or direct field
      const description =
        (typeof descriptionField === 'string'
          ? descriptionField
          : (descriptionField as any)?.['en-US']) || '';

      // Extract mime type
      const mimeType =
        fileField?.contentType ||
        (fileField as any)?.[0]?.contentType ||
        (fileField as any)?.['en-US']?.contentType;

      if (!imageUrl) return null;

      const fullUrl = imageUrl.startsWith('http')
        ? imageUrl
        : `https:${imageUrl}`;

      // Handle images
      if (mimeType?.startsWith('image/')) {
        return (
          <img
            src={fullUrl}
            alt={description || title || 'Image'}
            className='my-4 max-w-full rounded-lg'
          />
        );
      }

      // Handle other file types (PDFs, etc.)
      return (
        <a
          href={fullUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='text-primary underline'
        >
          {title || 'Download file'}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: () => {
      // Handle embedded entries if needed
      return null;
    },
    [INLINES.EMBEDDED_ENTRY]: () => {
      // Handle inline embedded entries if needed
      return null;
    },
    [INLINES.ASSET_HYPERLINK]: (node) => {
      const targetFields = node.data?.target?.fields;
      if (!targetFields) return null;

      // Handle both localized and non-localized field structures
      const fileField = targetFields.file;
      const titleField = targetFields.title;

      // Extract URL from localized or direct field
      const url =
        fileField?.url ||
        (fileField as any)?.[0]?.url ||
        (fileField as any)?.['en-US']?.url;

      // Extract title from localized or direct field
      const title =
        (typeof titleField === 'string'
          ? titleField
          : (titleField as any)?.['en-US']) || 'View asset';

      if (!url) return null;

      const fullUrl = url.startsWith('http') ? url : `https:${url}`;

      return (
        <a
          href={fullUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='text-primary underline'
        >
          {title}
        </a>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (_node, children) => {
      return (
        <span className='cursor-not-allowed text-primary underline'>
          {children}
        </span>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => {
      return (
        <a
          href={node.data.uri}
          target='_blank'
          rel='noopener noreferrer'
          className='text-primary underline'
        >
          {children}
        </a>
      );
    },
    [BLOCKS.PARAGRAPH]: (_node, children) => {
      return <p className='mb-2'>{children}</p>;
    },
    [BLOCKS.HEADING_1]: (_node, children) => {
      return <h1 className='mt-4 mb-3 text-2xl font-bold'>{children}</h1>;
    },
    [BLOCKS.HEADING_2]: (_node, children) => {
      return <h2 className='mt-3 mb-2 text-xl font-bold'>{children}</h2>;
    },
    [BLOCKS.HEADING_3]: (_node, children) => {
      return <h3 className='mt-2 mb-2 text-lg font-semibold'>{children}</h3>;
    },
    [BLOCKS.UL_LIST]: (_node, children) => {
      return (
        <ul className='mb-2 list-disc space-y-1 [&>li]:ml-4'>{children}</ul>
      );
    },
    [BLOCKS.OL_LIST]: (_node, children) => {
      return <ol className='mb-2 list-decimal space-y-1'>{children}</ol>;
    },
    [BLOCKS.LIST_ITEM]: (_node, children) => {
      return <li>{children}</li>;
    },
    [BLOCKS.QUOTE]: (_node, children) => {
      return (
        <blockquote className='my-4 border-l-4 border-gray-300 pl-4 italic'>
          {children}
        </blockquote>
      );
    },
    [BLOCKS.HR]: () => {
      return <hr className='my-4 border-gray-300' />;
    },
  },
};

/**
 * Renders Contentful rich text document with proper asset handling
 *
 * @param document - The Contentful rich text document
 * @returns Rendered React components
 */
export function renderRichText(document: Document) {
  return documentToReactComponents(document, richTextRenderOptions);
}
